import axios from 'axios';
import {
	API_ENDPOINTS,
	BottomAnchoredControls,
	Button,
	ButtonTypes,
	Card,
	CardBody,
	checkTheme,
	customToast,
	PageLayout,
	replaceKeyWithValue,
	route,
	SelectOption,
	StringHelpers,
	SubTitle,
	Themes,
	useDisabledContext,
	useForm,
	useLangContext,
	Variants,
	WidthConstrainedContainer,
} from 'carrier-fe';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const defaultData = {
	name: '',
	description: '',
	training_course_ids: [] as string[],
	event_at: '',
	end_date: '',
	training_venue_id: '',
	requires_payment: '0',
	price: '',
	currency: 'gbp',
	capacity: '',
	attendee_count: 0,
};

type Data = typeof defaultData;

function EventForm() {
	const { eventId } = useParams();
	const isEdit = eventId ? true : false;

	const isToshiba = checkTheme(Themes.Toshiba);

	const { crud, fields } = useLangContext();
	const { disabled } = useDisabledContext();
	const navigate = useNavigate();

	const [isFetching, setIsFetching] = useState<boolean>(false);

	const [courses, setCourses] = useState<SelectOption[]>([]);
	const [venues, setVenues] = useState<SelectOption[]>([]);
	const [currencies, setCurrencies] = useState<SelectOption[]>([]);

	useEffect(() => {
		if (isEdit) {
			fetchEventData();
		}
		fetchCourses();
		fetchVenues();
		fetchCurrencies();
	}, []);

	const fetchEventData = async () => {
		if (isFetching) return;
		setIsFetching(true);

		try {
			const { data: { data: trainingEventData } } = await axios.get<{ data: Data }>(
				route(API_ENDPOINTS.ADMIN.TRAINING.EVENT.EDIT, {
					trainingEvent: String(eventId),
				})
			);

			setValues(trainingEventData);
		} catch (err: any) {
			customToast({
				title:
					err?.response?.data?.message ||
					'An error occurred. Please try again.',
				variant: Variants.Danger,
			});
		} finally {
			setIsFetching(false);
		}
	};

	const fetchCourses = () => {
		axios
			.get(route(API_ENDPOINTS.GENERIC.SELECT.TRAINING.COURSES))
			.then((res) => setCourses(res.data.data));
	};

	const fetchVenues = () => {
		axios
			.get(route(API_ENDPOINTS.GENERIC.SELECT.TRAINING.VENUES))
			.then((res) => setVenues(res.data.data));
	};

	const fetchCurrencies = () => {
		axios
			.get(route(API_ENDPOINTS.GENERIC.SELECT.CURRENCIES))
			.then((res) => setCurrencies(res.data.data));
	};

	const updateEvent = async (payload: typeof defaultData) => {
		const { attendee_count, ...rest } = payload;

		const data = {
			...rest,
			training_course_ids: Array.isArray(rest.training_course_ids)
				? rest.training_course_ids
				: [rest.training_course_ids].filter(Boolean),
		};

		return isEdit
			? axios.patch<{ data: Data; message: string }>(
					route(API_ENDPOINTS.ADMIN.TRAINING.EVENT.UPDATE, {
						trainingEvent: String(eventId),
					}),
					data
			  )
			: axios.post<{ data: Data; message: string }>(
					route(API_ENDPOINTS.ADMIN.TRAINING.EVENT.STORE),
					data
			  );
	};

	const { Input, Submit, store, setValues, isSuccess, data } = useForm(
		updateEvent,
		defaultData
	);

	useEffect(() => {
		if (isSuccess && data) {
			const { id } = data;
			navigate(`/training/event/${id}`);
		}
	}, [isSuccess]);

	useEffect(() => {
		const courseIds = store.training_course_ids;
		const firstCourseId = Array.isArray(courseIds)
			? courseIds[0]
			: courseIds;

		if (!isEdit && firstCourseId) {
			const selectedCourse = courses?.find((course: SelectOption) => {
				return course.value === firstCourseId;
			});
			setValues({
				name: selectedCourse?.label,
				description: selectedCourse?.description,
			});
		}
	}, [store.training_course_ids]);

	useEffect(() => {
		if (store?.requires_payment === "0") {
			setValues({
				price: ''
			})
		}
	}, [store?.requires_payment]);

	let title = replaceKeyWithValue(
		crud?.pages?.create.title || 'Create :model',
		'model',
		crud?.models?.training_event || 'Training Event'
	);

	if (isEdit) {
		title = replaceKeyWithValue(
			crud?.pages?.edit.title || 'Edit :model',
			'model',
			crud?.models?.training_event || 'Training Event'
		);
	}

	return (
		<PageLayout title={StringHelpers.title(title)}>
			<WidthConstrainedContainer>
				<Card>
					<CardBody className={'pb-0'}>
						<SubTitle
							title={StringHelpers.title(
								crud?.sub_titles?.details || 'Details'
							)}
							className="mb-3"
							style={{ color: '#464C5E' }}
						/>

						{Input({
							name: 'training_course_ids',
							label: StringHelpers.title(
								isToshiba
									? fields?.training_courses || 'Courses'
									: fields?.training_course || 'Course'
							),
							type: 'searchable-select',
							isMulti: isToshiba,
							placeholder: StringHelpers.title(
								replaceKeyWithValue(
									crud?.placeholders?.search ||
										'Search :model',
									'model',
									crud?.models?.course || 'course'
								)
							),
							options: courses,
						})}

						{Input({
							name: 'name',
							label: StringHelpers.title(fields?.name || 'Name'),
						})}

						{Input({
							name: 'description',
							label: StringHelpers.title(
								fields?.description || 'Description'
							),
							type: 'textarea',
						})}

						{Input({
							name: 'event_at',
							label: StringHelpers.title(
								fields?.start_date || 'Start Date'
							),
							type: 'datetime',
						})}

						{Input({
							name: 'end_date',
							label: StringHelpers.title(
								fields?.end_date || 'End Date'
							),
							type: 'datetime',
						})}

						{Input({
							name: 'training_venue_id',
							label: StringHelpers.title(
								fields?.training_venue || 'Venue'
							),
							type: 'searchable-select',
							placeholder: StringHelpers.title(
								replaceKeyWithValue(
									crud?.placeholders?.search ||
										'Search :model',
									'model',
									crud?.models?.venue || 'Venue'
								)
							),
							options: venues,
						})}

						{Input({
							name: 'capacity',
							type: 'number',
							label: fields?.capacity || 'Capacity',
						})}
					</CardBody>
				</Card>
				<Card>
					<CardBody className={'pb-0'}>
						<SubTitle
							title={StringHelpers.title(
								crud?.sub_titles?.payment || 'Payment'
							)}
							className="mb-3"
							style={{ color: '#464C5E' }}
						/>

						{Input({
							name: 'requires_payment',
							label: StringHelpers.title(
								fields?.requires_payment || 'Requires Payment'
							),
							type: 'select',
							placeholder: StringHelpers.title(
								fields?.please_select || 'Please select'
							),
							options: crud?.options?.boolean || [],
							disable: store.attendee_count > 0,
						})}

						{store.requires_payment === '1' && (
							<>
								{Input({
									name: 'price',
									label: StringHelpers.title(
										fields?.price_incl_vat ||
											'Price (Inc VAT)'
									),
									disable: store.attendee_count > 0,
								})}

								{Input({
									name: 'currency',
									label: StringHelpers.title(
										fields?.currency || 'Currency'
									),
									type: 'select',
									placeholder: StringHelpers.title(
										fields?.please_select || 'Please select'
									),
									options: currencies,
									disable: store.attendee_count > 0,
								})}
							</>
						)}
					</CardBody>
				</Card>
			</WidthConstrainedContainer>
			<BottomAnchoredControls>
				<Button
					label={StringHelpers.title(
						crud?.buttons.back.default || 'Back'
					)}
					onClick={() =>
						isEdit
							? navigate(`/training/event/${eventId}`)
							: navigate('/training/event')
					}
					variant={Variants.Dark}
					type={ButtonTypes.Outline}
					className={'me-4'}
					disabled={disabled}
				/>
				<Submit
					defaultLabel={
						isEdit
							? crud?.buttons.edit.default || 'Update'
							: crud?.buttons.create.default || 'Create'
					}
					loadingLabel={
						isEdit
							? crud?.buttons.edit.submitting || 'Updating...'
							: crud?.buttons.create.submitting || 'Creating...'
					}
				/>
			</BottomAnchoredControls>
		</PageLayout>
	);
}

export default EventForm;
